import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { 
  ArrowRight, 
  Calculator, 
  FileText, 
  Building2, 
  Shield, 
  Users, 
  Scale,
  Check,
  Briefcase,
  TrendingUp,
  Clock,
  Search,
  UserCheck,
  RotateCcw
} from 'lucide-react';
import { SEO } from '@/components/SEO';

const microServices = [
  {
    icon: RotateCcw,
    title: 'Company Reset',
    idealFor: 'Founders with inactive, struck-off, or non-compliant companies under CCFS-2026',
    outcomes: 'Save up to 90% on penalties; clear compliance backlog; restart or close cleanly',
    features: ['CCFS-2026 scheme assessment', 'Pending filing & penalty resolution', 'Company closure or dormancy support', 'MCA status check & reset planning'],
    highlight: 'Government relief window open until 15 July 2026 — act now to save lakhs in penalties.',
    link: '/ccfs-2026-company-status-check',
  },
  {
    icon: Calculator,
    title: 'Accounting & Bookkeeping',
    idealFor: 'Businesses without a dedicated accountant or those with backlog issues',
    outcomes: 'Clean, up-to-date books; real-time financial visibility; audit-ready records',
    features: ['Monthly book closing', 'MIS reports', 'Bank reconciliation', 'Vendor & customer management'],
  },
  {
    icon: FileText,
    title: 'Tax & Compliance',
    idealFor: 'Businesses struggling with GST, TDS, or facing notice from tax authorities',
    outcomes: 'Zero missed deadlines; reduced tax liability; complete peace of mind',
    features: ['GST registration & filing', 'Income tax returns', 'TDS compliance', 'ROC filings'],
  },
  {
    icon: Building2,
    title: 'Registration Services',
    idealFor: 'Entrepreneurs starting out or formalizing an existing business',
    outcomes: 'Legally compliant structure; access to banking & funding; clear ownership',
    features: ['Company incorporation', 'LLP registration', 'Trust & Society registration', 'MSME/UDYAM registration'],
    highlight: 'Support with MSME/UDYAM registration and relevant government schemes for micro businesses and women entrepreneurs.',
  },
  {
    icon: Shield,
    title: 'Micro CFO',
    idealFor: 'Businesses without a finance head that need clarity, control, and decision support',
    outcomes: 'Clear understanding of business numbers; better cash control; confident decision-making; readiness for bank funding',
    features: ['Understanding where money is coming from and going', 'Monthly clarity on profit, cash, and working capital', 'Decision support for growth and spending', 'Preparation for bank funding and financial discipline'],
    highlight: 'Preparing your business for bank funding with financial statements, projections, and documentation.',
  },
];

const cxoRoles = [
  {
    title: 'CFO & Finance Leaders',
    roles: 'Chief Financial Officers, Finance Directors, VP Finance',
    sectors: 'Corporates, startups, PE/VC-backed companies',
  },
  {
    title: 'Tax & Compliance Heads',
    roles: 'Tax Directors, Head of Indirect Taxes, Compliance Officers',
    sectors: 'Complex regulatory environments, multinationals',
  },
  {
    title: 'Senior Legal Counsel',
    roles: 'General Counsel, Legal Directors, Company Secretaries',
    sectors: 'M&A-active firms, regulated industries',
  },
];

const processSteps = [
  { step: '01', title: 'Discovery', desc: '60-90 minute structured discussion + document checklist to understand your business', duration: '1-2 days' },
  { step: '02', title: 'Assessment', desc: 'Detailed analysis of current state, gap identification, and opportunity mapping', duration: '3-5 days' },
  { step: '03', title: 'Execution', desc: 'Implementation with dedicated point of contact and regular progress updates', duration: 'Ongoing' },
  { step: '04', title: 'Review', desc: 'Monthly/quarterly reviews with actionable insights and course corrections', duration: 'Continuous' },
];

export default function ServicesPage() {
  return (
    <div className="pt-20" data-testid="services-page">
      <SEO 
        title="Services - Accounting, Tax, Micro CFO & CXO Hiring"
        path="/services"
        description="Comprehensive accounting, tax compliance, Micro CFO, MSME registration, and CXO hiring services for small businesses under ₹10 Crore. Simple pricing, expert guidance."
        keywords="accounting services India, GST filing, Micro CFO small business, tax compliance, MSME registration, CXO hiring, company registration"
      />
      {/* Hero */}
      <section className="py-20 md:py-28 bg-[#F9F9F7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <span className="text-sm font-medium tracking-widest text-[#E84C3D] uppercase mb-4 block">Our Services</span>
          <h1 className="font-secondary text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Two Verticals, One Mission
          </h1>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto mb-6">
            Clean books, zero compliance stress, and the right finance leaders when you need them — for businesses up to ₹10 Crore and non-profits.
          </p>
          <Link to="/contact">
            <Button className="bg-[#E84C3D] hover:bg-[#C0392B] text-white px-6 py-5">
              Schedule a Free Consultation <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Micro Business Services */}
      <section className="py-20 md:py-28 bg-white" data-testid="micro-services-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-12">
            <div className="inline-block px-4 py-1 bg-[#E84C3D] text-white rounded-full text-sm font-medium mb-4">
              For Micro Businesses & Non-profits
            </div>
            <h2 className="font-secondary text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
              Complete Business Support
            </h2>
            <p className="text-lg text-slate-600 max-w-3xl">
              Expert MBAs and Chartered Accountants serving manufacturers, service providers, and traders. 
              We handle the financial complexity and formalisation so you can focus on growth.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {microServices.map((service, index) => {
              const Icon = service.icon;
              return (
                <div 
                  key={index}
                  className="bg-[#F9F9F7] p-8 rounded-xl hover:shadow-xl transition-shadow"
                  data-testid={`micro-service-${index}`}
                >
                  <div className="w-14 h-14 rounded-xl bg-[#E84C3D]/10 flex items-center justify-center mb-5">
                    <Icon className="w-7 h-7 text-[#E84C3D]" />
                  </div>
                  <h3 className="font-semibold text-xl text-slate-900 mb-4">{service.title}</h3>
                  
                  {/* Ideal For */}
                  <div className="mb-4">
                    <p className="text-xs font-semibold text-[#E84C3D] uppercase tracking-wide mb-1">Ideal For</p>
                    <p className="text-sm text-slate-600">{service.idealFor}</p>
                  </div>

                  {/* Key Outcomes */}
                  <div className="mb-4">
                    <p className="text-xs font-semibold text-[#059669] uppercase tracking-wide mb-1">Key Outcomes</p>
                    <p className="text-sm text-slate-700 font-medium">{service.outcomes}</p>
                  </div>

                  {/* Features */}
                  <ul className="space-y-2 mb-4">
                    {service.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2 text-slate-600 text-sm">
                        <Check className="w-4 h-4 text-[#059669] mt-0.5 shrink-0" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Highlight for government schemes/bank funding */}
                  {service.highlight && (
                    <div className="bg-[#059669]/10 p-3 rounded-lg">
                      <p className="text-sm text-[#059669] font-medium">{service.highlight}</p>
                    </div>
                  )}

                  {service.link && (
                    <Link to={service.link} className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-[#E84C3D] hover:text-[#C0392B] transition-colors">
                      Check Your Savings <ArrowRight className="w-4 h-4" />
                    </Link>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-10 text-center">
            <Link to="/contact?service=micro-business">
              <Button className="bg-[#E84C3D] hover:bg-[#C0392B] text-white px-6 py-5">
                Get Started with Business Services <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CXO Hiring Services */}
      <section className="py-20 md:py-28 bg-slate-900 text-white" data-testid="cxo-services-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="mb-12">
            <div className="inline-block px-4 py-1 bg-[#059669] text-white rounded-full text-sm font-medium mb-4">
              For Large Corporates & Funds
            </div>
            <h2 className="font-secondary text-3xl md:text-4xl font-semibold mb-4">
              Executive Hiring Excellence
            </h2>
            <p className="text-lg text-slate-400 max-w-3xl">
              Specialized CXO search focusing on CFO, Tax Heads, and Senior Legal positions. 
              We leverage deep industry networks to find exceptional talent.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {cxoRoles.map((role, index) => (
              <div 
                key={index}
                className="bg-slate-800 p-6 rounded-xl"
                data-testid={`cxo-role-${index}`}
              >
                <h3 className="font-semibold text-lg mb-3">{role.title}</h3>
                <p className="text-sm text-slate-300 mb-2">{role.roles}</p>
                <p className="text-xs text-slate-400">{role.sectors}</p>
              </div>
            ))}
          </div>

          {/* Process Assurance */}
          <div className="bg-slate-800 p-8 rounded-xl">
            <h3 className="font-semibold text-xl mb-6">Our Hiring Process</h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-[#059669]/20 flex items-center justify-center shrink-0">
                  <Clock className="w-5 h-5 text-[#34D399]" />
                </div>
                <div>
                  <p className="font-medium text-sm">Average Shortlist</p>
                  <p className="text-slate-400 text-xs">2-3 weeks</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-[#059669]/20 flex items-center justify-center shrink-0">
                  <Search className="w-5 h-5 text-[#34D399]" />
                </div>
                <div>
                  <p className="font-medium text-sm">Deep Reference Checks</p>
                  <p className="text-slate-400 text-xs">3-5 references per candidate</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-[#059669]/20 flex items-center justify-center shrink-0">
                  <UserCheck className="w-5 h-5 text-[#34D399]" />
                </div>
                <div>
                  <p className="font-medium text-sm">Quality Shortlist</p>
                  <p className="text-slate-400 text-xs">3-5 vetted candidates</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-[#059669]/20 flex items-center justify-center shrink-0">
                  <Shield className="w-5 h-5 text-[#34D399]" />
                </div>
                <div>
                  <p className="font-medium text-sm">Replacement Guarantee</p>
                  <p className="text-slate-400 text-xs">6-month assurance</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-10 text-center">
            <Link to="/contact?service=executive-hiring">
              <Button className="bg-white text-slate-900 hover:bg-slate-100 px-6 py-5">
                Discuss Your Hiring Needs <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section className="py-20 md:py-28 bg-[#F9F9F7]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-12">
            <span className="text-sm font-medium tracking-widest text-[#E84C3D] uppercase mb-4 block">Process</span>
            <h2 className="font-secondary text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
              How We Work
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              A structured approach that ensures clarity, accountability, and results at every step.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {processSteps.map((item, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-sm" data-testid={`process-step-${index}`}>
                <div className="w-14 h-14 rounded-full bg-[#F9F9F7] flex items-center justify-center mx-auto mb-4">
                  <span className="font-secondary text-xl font-bold text-[#E84C3D]">{item.step}</span>
                </div>
                <h3 className="font-semibold text-lg text-slate-900 mb-2 text-center">{item.title}</h3>
                <p className="text-slate-600 text-sm text-center mb-3">{item.desc}</p>
                <p className="text-xs text-[#059669] font-medium text-center">{item.duration}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-secondary text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
            Let's Discuss Your Needs
          </h2>
          <p className="text-lg text-slate-600 mb-8">
            Every business is unique. Let's have a conversation about how we can tailor our services to your specific requirements.
          </p>
          <Link to="/contact">
            <Button className="bg-[#E84C3D] hover:bg-[#C0392B] text-white px-8 py-6 text-base" data-testid="services-cta-btn">
              Schedule a Free Consultation <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
